"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CashierSyncRecord = exports.CashierLoginQrIssued = exports.CashierLoginQrStatus = exports.CashierLoginQrTokenHash = exports.CashierLoginQrPayload = exports.CASHIER_LOGIN_QR_PREFIX = exports.CashierListResponse = exports.CashierPinRotate = exports.CashierPatch = exports.CashierProvisioned = exports.CashierCreate = exports.CashierAdmin = exports.CashierManagedBy = exports.CashierLocationRef = exports.CashierGpStore = exports.CashierRoleRef = exports.RotationKind = exports.CashierPin = exports.CashierLoginName = exports.EmployeeCode = exports.CashierStatus = void 0;
const zod_1 = require("zod");
/**
 * Cashier contracts (Sprint 5).
 *
 * The cashier directory authored on the Backoffice and synced to
 * terminals. Sprint 5 ships the PIN credential method only (4–8
 * digits, Argon2id-hashed with per-tenant pepper). Card / badge /
 * iButton land in Sprint 7+ per dev-plan.
 *
 * See `apps/bos/docs/modules/employees.md` for the full spec.
 */
// ---------- Primitive shapes ----------
exports.CashierStatus = zod_1.z.enum(['active', 'suspended']);
/**
 * Employee code — retailer-assigned identifier (badge number,
 * personnel code, etc.). Distinct from the cuid `id`. Used by the
 * terminal "switch cashier" UX where typing the full id would be
 * impractical.
 */
exports.EmployeeCode = zod_1.z
    .string()
    .min(2)
    .max(32)
    .regex(/^[A-Za-z0-9_-]+$/, 'alphanumeric / underscore / dash only');
/** GP00213.pos.cashiers.cashierloginname — credential encoded in Yuki QR badges. */
exports.CashierLoginName = zod_1.z.string().trim().min(1).max(120);
/**
 * Cleartext PIN — 4..8 digits per TRS §28.1. NEVER persisted in
 * cleartext; the BOS stores only the Argon2id hash. This shape is
 * the wire input to provisioning / rotation endpoints.
 */
exports.CashierPin = zod_1.z.string().regex(/^\d{4,8}$/, 'PIN must be 4..8 digits');
exports.RotationKind = zod_1.z.enum(['initial_provision', 'admin_reset', 'expired']);
/**
 * The role assigned to a cashier (RFL-207). References a Role authored in
 * the Roles section; drives the cashier's effective permissions. Nullable —
 * a cashier may have no role assigned.
 */
exports.CashierRoleRef = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
});
/** GP-supplied store display data; does not grant access or replace home location. */
exports.CashierGpStore = zod_1.z.object({
    code: zod_1.z.string(),
    id: zod_1.z.string().nullable(),
    name: zod_1.z.string().nullable(),
    active: zod_1.z.boolean(),
});
/**
 * VL-350: the cashier's home store (`Cashier.locationId`) as the operator
 * knows it — store number (`Location.code`) and name. Null when the cashier is
 * not store-restricted.
 */
exports.CashierLocationRef = zod_1.z.object({
    id: zod_1.z.string(),
    code: zod_1.z.string(),
    name: zod_1.z.string(),
});
/**
 * VL-351: the integration that owns a cashier and keeps it in step with the
 * store system. Such a cashier is read-only in Back Office: the employees API
 * refuses every change to it with `IMPORTED_READ_ONLY`, and only the import
 * (or the store Link's relay) writes it. `sourceName` is the integration
 * source's operator-given name, never a product name.
 */
exports.CashierManagedBy = zod_1.z.object({
    kind: zod_1.z.literal('store-system'),
    sourceId: zod_1.z.string(),
    sourceName: zod_1.z.string(),
});
// ---------- Admin REST surface ----------
/**
 * Admin GET / list response shape (per-row). PIN hash + cleartext
 * NEVER present in any admin GET response. The cleartext is
 * returned exactly once at provisioning time via `CashierProvisioned`.
 */
exports.CashierAdmin = zod_1.z.object({
    id: zod_1.z.string(),
    employeeCode: exports.EmployeeCode,
    loginName: exports.CashierLoginName.nullable(),
    displayName: zod_1.z.string().min(1).max(120),
    status: exports.CashierStatus,
    // RFL-124: cashier may act as an SCO attendant (approves self-checkout
    // interventions). Default false; terminal-side enforcement lands later.
    isAttendant: zod_1.z.boolean(),
    // RFL-207: the cashier's assigned role (from the Roles section), or null.
    role: exports.CashierRoleRef.nullable(),
    gpStores: zod_1.z.array(exports.CashierGpStore).optional(),
    gpStoresSynced: zod_1.z.boolean().optional(),
    // VL-350: home store, or null when the cashier is not store-restricted.
    // Optional so a response from a host before VL-350 still parses.
    location: exports.CashierLocationRef.nullable().optional(),
    // VL-350: the store system's own cashier group (e.g. OP, SO) for an
    // imported cashier; null for every other cashier.
    storeGroup: zod_1.z.string().nullable().optional(),
    // VL-351: set when the store system owns this cashier (read-only here).
    managedBy: exports.CashierManagedBy.nullable().optional(),
    // VL-351: even a disabled source's imported cashier keeps its store role.
    roleReadOnly: zod_1.z.boolean().optional(),
    pinUpdatedAt: zod_1.z.string().datetime().nullable(),
    pinResetCount: zod_1.z.number().int().min(0),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
/**
 * POST /cashiers body. PIN optional — when omitted the server
 * generates a random 4-digit PIN.
 */
exports.CashierCreate = zod_1.z.object({
    employeeCode: exports.EmployeeCode,
    loginName: exports.CashierLoginName.nullable().optional(),
    displayName: zod_1.z.string().min(1).max(120),
    pin: exports.CashierPin.optional(),
    // RFL-207: optionally assign a role at creation. Omit / null for none.
    roleId: zod_1.z.string().min(1).nullable().optional(),
});
/**
 * Response to a successful POST /cashiers. The `pin` field is the
 * cleartext PIN the operator must hand to the cashier. NEVER
 * surfaces again — repeat reads of the cashier omit it.
 */
exports.CashierProvisioned = zod_1.z.object({
    cashier: exports.CashierAdmin,
    pin: exports.CashierPin,
    pinProvisionedAt: zod_1.z.string().datetime(),
});
/**
 * PATCH /cashiers/:id body. Display-data + suspend toggle only.
 * PIN rotation is a separate endpoint with stricter permissions.
 */
exports.CashierPatch = zod_1.z.object({
    loginName: exports.CashierLoginName.nullable().optional(),
    displayName: zod_1.z.string().min(1).max(120).optional(),
    status: exports.CashierStatus.optional(),
    isAttendant: zod_1.z.boolean().optional(),
    // RFL-207: assign / change (string) or clear (null) the cashier's role.
    // Omitted = leave unchanged.
    roleId: zod_1.z.string().min(1).nullable().optional(),
});
/**
 * POST /cashiers/:id/pin body. Empty body / omitted `pin` triggers
 * server-generated random PIN.
 */
exports.CashierPinRotate = zod_1.z.object({
    pin: exports.CashierPin.optional(),
});
exports.CashierListResponse = zod_1.z.object({
    cashiers: zod_1.z.array(exports.CashierAdmin),
    page: zod_1.z.number().int().min(1),
    pageSize: zod_1.z.number().int().min(1),
    total: zod_1.z.number().int().min(0),
});
// ---------- Login QR code (VL-349) ----------
/**
 * VL-349: the text a cashier's login QR code carries.
 *
 * `73491` (the format tag) and 78 decimal digits: 32 random bytes, 256 bits,
 * as one zero-padded number, issued by Back Office for one cashier. It is an
 * opaque bearer credential and nothing else — not the PIN, not its hash, not
 * the employee code, not the name — so a photographed code tells nobody who it
 * belongs to, and regenerating it is the only way to change it.
 *
 * Digits only, on purpose: the stores' scanners are keyboard wedges, and a
 * wedge types letters through whatever keyboard layout the till has active —
 * under a Ukrainian layout a Latin letter arrives as a Cyrillic one. Digits
 * come through every layout unchanged, and the QR numeric mode keeps the
 * image small.
 */
exports.CASHIER_LOGIN_QR_PREFIX = '73491';
exports.CashierLoginQrPayload = zod_1.z
    .string()
    .regex(/^73491[0-9]{78}$/, 'not a cashier login QR payload');
/**
 * VL-349: SHA-256 of the UTF-8 payload, lower-case hex. The only form of the
 * credential Back Office stores and the only form a till is sent; a till
 * hashes what it scans and compares. Not peppered: the payload carries 256
 * random bits, so the hash cannot be searched back to it.
 */
exports.CashierLoginQrTokenHash = zod_1.z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'SHA-256 hex, lower case');
/** GET /employees/cashiers/:id/login-qr — whether a code is valid, never the code. */
exports.CashierLoginQrStatus = zod_1.z.object({
    cashierId: zod_1.z.string(),
    /** False until the first code is generated. */
    active: zod_1.z.boolean(),
    /** 1 for the first code, +1 per regeneration; null when none is active. */
    version: zod_1.z.number().int().min(1).nullable(),
    generatedAt: zod_1.z.string().datetime().nullable(),
});
/**
 * POST /employees/cashiers/:id/login-qr — a new code. `payload` is returned
 * by this response only; Back Office keeps its hash and cannot show it again.
 */
exports.CashierLoginQrIssued = exports.CashierLoginQrStatus.extend({
    active: zod_1.z.literal(true),
    version: zod_1.z.number().int().min(1),
    generatedAt: zod_1.z.string().datetime(),
    payload: exports.CashierLoginQrPayload,
    /** The previous code's version, now revoked; null on the first generation. */
    revokedVersion: zod_1.z.number().int().min(1).nullable(),
});
// ---------- Sync-down wire shape (terminal-facing) ----------
/**
 * Per-record payload of `/sync/cashiers` delta. Includes the
 * Argon2id `pinHash` (terminals verify locally per TRS §28.1) but
 * NOT the cleartext or any admin-only diagnostics.
 */
exports.CashierSyncRecord = zod_1.z.object({
    id: zod_1.z.string(),
    employeeCode: exports.EmployeeCode,
    loginName: exports.CashierLoginName.nullable().optional(),
    displayName: zod_1.z.string().min(1).max(120),
    status: exports.CashierStatus,
    pinHash: zod_1.z.string().nullable(),
    /**
     * RFL-284 — which pepper produced `pinHash`.
     *
     * 0 means the legacy deployment-wide `CASHIER_PIN_PEPPER`; 1+ means a
     * per-tenant key derived by HKDF (ADR-0011). The terminal MUST select
     * its verification key by this value rather than assuming one pepper
     * for the whole device: during a tenant's migration, cashiers on the
     * same till legitimately carry different versions, and guessing means
     * a cashier cannot log in.
     *
     * Optional on the wire so a terminal running the previous contract
     * keeps syncing; it will read `undefined` and fall back to version 0,
     * which is exactly right for a tenant that has not started rotating.
     */
    pinKeyVersion: zod_1.z.number().int().min(0).optional(),
    isAttendant: zod_1.z.boolean(),
    /**
     * RFL-227: the cashier's assigned role id (null = no role), authored in
     * Back Office (RFL-207). Additive — terminals that predate role
     * enforcement ignore it.
     */
    roleId: zod_1.z.string().nullable(),
    /**
     * RFL-227: the effective permission codes of the assigned role, resolved
     * server-side so the till enforces without a second lookup. Empty when
     * the assigned role grants nothing. Sorted for stable diffing.
     *
     * RL-483: no longer empty for a role-less cashier — the host grants such a
     * cashier every till action explicitly (`ROLELESS_CASHIER_TILL_GRANTS` in
     * `@reform-lite/connectors`, today's till rights, never
     * `till.confirmation.exempt`), so the till never has to guess what "no role"
     * means.
     */
    permissionCodes: zod_1.z.array(zod_1.z.string()),
    /**
     * RL-483: `true` when `permissionCodes` is the cashier's COMPLETE till grant.
     * A till that sees it permits exactly the listed codes and nothing else,
     * whatever `roleId` says — no role, or a role id it has never heard of,
     * grants nothing that is not listed.
     *
     * Optional so a record from a host that predates it still parses; a till
     * reading such a record keeps the pre-RL-483 rule ("no role = every till
     * action"), because that host still sends role-less cashiers an empty list
     * and failing closed on it would lock a role-less estate out of X and Z.
     */
    permissionCodesAuthoritative: zod_1.z.boolean().optional(),
    /**
     * VL-73: the vocabulary revision of `permissionCodes`. 2: `till.receipt.cancel`
     * (cancel a whole receipt) is its own grant, apart from `till.line.void`
     * (remove a line). Absent on a record from a host before the split, where
     * `till.line.void` covered both — a till reads such a record's
     * `till.line.void` as granting `till.receipt.cancel` too, so upgrading a
     * till before its host takes nobody's right away.
     */
    permissionCodesRevision: zod_1.z.number().int().min(1).optional(),
    /**
     * VL-73: the login this cashier types on the till when it is not the
     * employee code — the store system's own login for a connector-imported
     * cashier (`t_kassirs.login`), null for every other cashier. Not unique
     * across a tenant (the same login legitimately exists in two stores), so a
     * till matches it only among the cashiers of its own store, which is all a
     * host sends it, and refuses a login two of them share. Absent from an
     * older host's records.
     */
    storeLogin: zod_1.z.string().min(1).max(512).nullable().optional(),
    /**
     * VL-349: the hash of the cashier's valid login QR code
     * ({@link CashierLoginQrTokenHash}), sent only when one is valid. A till
     * signs in whoever's record carries the hash of what it scanned, with no
     * PIN, through the same refusals as any other sign-in (suspended,
     * approval-only, locked out). Absent means no code: a till reads it as
     * null and forgets the previous one, which is how a regenerated code
     * revokes the old one on the next sync. The hash travels inside the
     * cashier record, so it reaches exactly the tills the cashier does.
     */
    qrLoginTokenHash: exports.CashierLoginQrTokenHash.optional(),
});
//# sourceMappingURL=cashier.js.map